import { StorageKeys, readStorage, writeStorage, removeStorage } from './localStorage';
import type { UiPrefs, ThemeName } from '@/types';
import type { Progress } from '@/types';
import { defaultProgress, defaultAchievements } from '@/contexts/defaults';

/**
 * One-time migration from legacy `qa_studio.html` localStorage keys to the
 * consolidated `qastudio.v1.*` namespace. Safe to call repeatedly — no-op
 * after first run sets a marker key.
 */
const MIGRATION_MARKER = 'qastudio.v1.__migrated';

interface LegacyKeys {
  theme?: string;
  density?: string;
  lastSprint?: string;
  sprint0Completed?: string;
  ticketStatePrefix?: string;
}

const LEGACY: LegacyKeys = {
  theme: 'qaStudio_theme',
  density: 'qaStudio_density',
  lastSprint: 'qaStudio_lastSprint',
  sprint0Completed: 'qaStudio_sprint00_v1',
  ticketStatePrefix: 'qaStudio_ticketState_',
};

const themeFromLegacy = (raw: string | null): ThemeName | null => {
  if (!raw) return null;
  if (raw === 'dark' || raw === 'deep' || raw === 'deepstudio') return 'deepstudio';
  if (raw === 'mint' || raw === 'coolmist') return 'coolmist';
  if (raw === 'warm' || raw === 'light' || raw === 'warmsunset') return 'warmsunset';
  return null;
};

export function migrateOldState(): { migrated: boolean; details: string[] } {
  const details: string[] = [];
  if (typeof window === 'undefined') return { migrated: false, details };
  if (window.localStorage.getItem(MIGRATION_MARKER)) {
    return { migrated: false, details: ['already-migrated'] };
  }

  const ui = readStorage<UiPrefs | null>(StorageKeys.ui, null);
  const progress = readStorage<Progress | null>(StorageKeys.progress, null);

  const newUi: UiPrefs = ui ?? { theme: 'warmsunset', density: 'cosy', decoration: 'playful' };
  let touchedUi = false;

  const legacyTheme = themeFromLegacy(window.localStorage.getItem(LEGACY.theme!));
  if (legacyTheme && newUi.theme !== legacyTheme) {
    newUi.theme = legacyTheme;
    touchedUi = true;
    details.push(`theme ← ${legacyTheme}`);
  }

  const legacyDensity = window.localStorage.getItem(LEGACY.density!);
  if (legacyDensity === 'compact' && newUi.density !== 'compact') {
    newUi.density = 'compact';
    touchedUi = true;
    details.push('density ← compact');
  }

  if (touchedUi) writeStorage(StorageKeys.ui, newUi);

  const newProgress: Progress = progress
    ? {
        ...defaultProgress(),
        ...progress,
        cockpit: progress.cockpit ?? { mockHistory: {}, flashcards: {} },
        achievements: progress.achievements ?? defaultAchievements(),
        capstone: progress.capstone ?? {},
      }
    : defaultProgress();
  let touchedProgress = false;

  const legacyLast = window.localStorage.getItem(LEGACY.lastSprint!);
  if (legacyLast && newProgress.last.sprintId === defaultProgress().last.sprintId) {
    newProgress.last.sprintId = legacyLast;
    touchedProgress = true;
    details.push(`last.sprintId ← ${legacyLast}`);
  }

  const sprint0 = window.localStorage.getItem(LEGACY.sprint0Completed!);
  if (sprint0) {
    try {
      const parsed = JSON.parse(sprint0) as { completed?: boolean };
      if (parsed.completed && !newProgress.onboarding.sprint0TourCompleted) {
        newProgress.onboarding.sprint0TourCompleted = true;
        touchedProgress = true;
        details.push('sprint0TourCompleted ← true');
      }
    } catch {
      /* malformed legacy json — skip */
    }
  }

  if (touchedProgress) writeStorage(StorageKeys.progress, newProgress);

  // Mark complete · cleanup happens after 30-day grace (Phase 2+).
  window.localStorage.setItem(MIGRATION_MARKER, new Date().toISOString());

  return { migrated: details.length > 0, details };
}

/** For tests: clear the marker so migration can run again. */
export function resetMigrationMarker(): void {
  if (typeof window === 'undefined') return;
  removeStorage(MIGRATION_MARKER);
}
